import React from 'react';

import { Badge, Box, Divider, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import dayjs from 'dayjs';
import useAthletePackage from 'hooks/athletePackage/useAthletePackage';

import { useCancelModal } from '../../CancelModalContext';

const ReviewRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Group position='apart' noWrap align='flex-start'>
    <Text size='sm' color='dimmed' sx={{ flexShrink: 0 }}>
      {label}
    </Text>
    <Text size='sm' fw={500} align='right'>
      {value}
    </Text>
  </Group>
);

const getFormattedEndDate = (cancelNow: boolean, cancellationDate: Date | null): string => {
  if (cancelNow) return 'Immediately';
  if (cancellationDate) return dayjs(cancellationDate).format('MMM D, YYYY');
  return '—';
};

const ReviewStep: React.FC = () => {
  const { form, athletePackage, upcomingCreditedCount, upcomingUncreditedCount, upcomingTotalCount } =
    useCancelModal();

  const { cancelNow, cancellationDate, athleteStatusOnCancel, cancellationReason, reservationChoice } =
    form.values;

  const parentQuery = useAthletePackage(athletePackage.parent?.athletePackageId);
  const parentPackage = parentQuery.data?.data.package;
  const parentName = parentPackage?.name;
  const parentAutoRenewOn = parentPackage?.autoRenew === true;

  const formattedEndDate = getFormattedEndDate(cancelNow, cancellationDate);

  const willKeep = reservationChoice === 'KEEP';
  const willDelete = reservationChoice === 'DELETE';
  const showStatusChange = athleteStatusOnCancel !== 'No status change';

  const hasWarnings =
    (willKeep && upcomingCreditedCount > 0) ||
    (willDelete && upcomingTotalCount > 0) ||
    (willKeep && upcomingUncreditedCount > 0) ||
    (!!athletePackage.parent && parentAutoRenewOn);

  return (
    <Stack spacing='md'>
      <Paper p='md' radius='md' withBorder>
        <Stack spacing='xs'>
          <Text size='xs' tt='uppercase' fw={600} color='dimmed' lts={0.5}>
            Cancellation Summary
          </Text>

          <Divider />

          <Stack spacing='sm'>
            <ReviewRow
              label='Effective date'
              value={
                cancelNow ? (
                  <Badge color='red' variant='light' size='sm'>
                    Immediately
                  </Badge>
                ) : (
                  formattedEndDate
                )
              }
            />

            {cancellationReason && (
              <>
                <Divider variant='dashed' />
                <ReviewRow label='Reason' value={cancellationReason} />
              </>
            )}

            {reservationChoice && (
              <>
                <Divider variant='dashed' />
                <ReviewRow
                  label='Reservations'
                  value={
                    willKeep ? (
                      <Badge color='yellow' variant='light' size='sm'>
                        Uncredit &amp; keep
                      </Badge>
                    ) : (
                      <Badge color='red' variant='light' size='sm'>
                        Permanently delete
                      </Badge>
                    )
                  }
                />
              </>
            )}

            {showStatusChange && (
              <>
                <Divider variant='dashed' />
                <ReviewRow label='New athlete status' value={athleteStatusOnCancel} />
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      {hasWarnings && (
        <Paper
          p='md'
          radius='md'
          withBorder
          sx={theme => ({
            borderColor: theme.colors.yellow[4],
            backgroundColor: theme.colors.yellow[0],
          })}
        >
          <Stack spacing='sm'>
            <Group spacing='xs'>
              <ThemeIcon color='yellow' variant='light' size='sm' radius='xl'>
                <IconAlertTriangle size={12} />
              </ThemeIcon>
              <Text size='xs' fw={600} color='yellow.8' tt='uppercase' lts={0.5}>
                What will happen
              </Text>
            </Group>

            <Stack spacing={6}>
              {willKeep && upcomingCreditedCount > 0 && (
                <Group spacing='xs' noWrap align='flex-start'>
                  <Box
                    mt={6}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: 'currentColor',
                      color: '#f59f00',
                      flexShrink: 0,
                    }}
                  />
                  <Text size='sm'>{upcomingCreditedCount} credited reservation(s) will be uncredited.</Text>
                </Group>
              )}

              {willDelete && upcomingTotalCount > 0 && (
                <Group spacing='xs' noWrap align='flex-start'>
                  <Box
                    mt={6}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: 'currentColor',
                      color: '#f59f00',
                      flexShrink: 0,
                    }}
                  />
                  <Text size='sm'>{upcomingTotalCount} reservation(s) will be permanently deleted.</Text>
                </Group>
              )}

              {willKeep && upcomingUncreditedCount > 0 && (
                <Group spacing='xs' noWrap align='flex-start'>
                  <Box
                    mt={6}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: 'currentColor',
                      color: '#f59f00',
                      flexShrink: 0,
                    }}
                  />
                  <Text size='sm'>
                    {upcomingUncreditedCount} uncredited reservation(s) will remain as-is.
                  </Text>
                </Group>
              )}

              {athletePackage.parent && parentAutoRenewOn && (
                <Group spacing='xs' noWrap align='flex-start'>
                  <Box
                    mt={6}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      backgroundColor: 'currentColor',
                      color: '#f59f00',
                      flexShrink: 0,
                    }}
                  />
                  <Text size='sm'>
                    Auto-Renew will be disabled on <strong>{parentName ?? 'the parent package'}</strong>.
                  </Text>
                </Group>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      <KiloAlert variantStyle='error'>
        <Group noWrap>
          <Box>
            <Text size='sm' fw={500}>
              This action cannot be undone.
            </Text>
            <Text size='xs' color='dimmed'>
              Changes to reservations and credits will take effect immediately.
            </Text>
          </Box>
        </Group>
      </KiloAlert>
    </Stack>
  );
};

export default ReviewStep;
